import { useState } from "react";
import type { PagyMetadata } from "@/components/pagination/PaginationControls";
import PaginationControls from "@/components/pagination/PaginationControls";
import PageHeader from "@/components/PageHeader";
import Tags from "@/components/Tags";
import DataTable, { DataTableColumn } from "@/components/DataTable";
import TableSearch from "@/components/inputs/TableSearch";
import { PageWithLayout, wideLayout } from "@/components/Layout";

interface Artist {
  name: string;
}

interface Track {
  id: number;
  title: string;
  primary_genre_label: string | null;
  themes: string | null;
  moods: string | null;
  artist: Artist;
}

interface IndexProps {
  tracks: Track[];
  title_q: string | null;
  pagy: PagyMetadata;
}

const searchReloadProps = ["tracks", "title_q", "pagy"];

const parseTags = (value: string | null): string[] =>
  value
    ? value
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

const columns: DataTableColumn<Track>[] = [
  {
    label: "Title",
    minWidth: "min-w-48",
    cellClassName: "font-medium",
    render: (track) => track.title,
  },
  {
    label: "Artist",
    minWidth: "min-w-40",
    cellClassName: "font-light",
    render: (track) => track.artist.name,
  },
  {
    label: "Primary Genre",
    minWidth: "min-w-36",
    cellClassName: "font-light",
    render: (track) => track.primary_genre_label,
  },
  {
    label: "Themes",
    minWidth: "min-w-40",
    clamp: false,
    render: (track) => (
      <Tags tags={parseTags(track.themes)} maxVisible={1} noWrap />
    ),
  },
  {
    label: "Moods",
    minWidth: "min-w-40",
    clamp: false,
    render: (track) => (
      <Tags tags={parseTags(track.moods)} maxVisible={1} noWrap />
    ),
  },
];

const Index: PageWithLayout<IndexProps> = ({ tracks, title_q, pagy }) => {
  const [searching, setSearching] = useState(false);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-10">
      <PageHeader
        title="All tracks"
        primaryAction={{ href: "/tracks/new", text: "New Track" }}
      />
      <div className="flex w-full flex-col items-start justify-start gap-5">
        <div className="w-full md:max-w-sm">
          <TableSearch
            placeholder="Search by title"
            queryParam="title_q"
            initialValue={title_q ?? ""}
            only={searchReloadProps}
            onLoadingChange={setSearching}
          />
        </div>
        <DataTable
          columns={columns}
          rows={tracks}
          rowKey={(track) => track.id}
          rowHref={(track) => `/tracks/${track.id}`}
          emptyMessage="No tracks found"
          loading={searching}
        />
        <PaginationControls {...pagy} />
      </div>
    </div>
  );
};

Index.layout = wideLayout;

export default Index;
